import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Divider,
} from "@mui/material";
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import { DataGrid } from "@mui/x-data-grid";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import axiosInstance from "./axiosInstance";
import TimeEntryForm from "./TimeEntryForm";
import { ResponsiveCalendar } from '@nivo/calendar';
import { useTheme } from '@mui/material/styles';
import nivoTheme from '../theme/nivoTheme';
import dayjs from 'dayjs';

import 'dayjs/locale/de';

import InlineEdit from './ui/InlineEdit';
import { useBreadcrumbs } from './BreadcrumbBar';
import EmptyState from './ui/EmptyState';

// Leerzustand für ein leeres Such-/Filterergebnis (D4)
const NoResultsOverlay = () => (
  <EmptyState title="Nichts gefunden." hint="Such- oder Filterkriterien anpassen." />
);


const getStatusColor = (status) => {
  switch(status) {
    case 'Active': return 'success';
    case 'Completed': return 'info';
    case 'Cancelled': return 'error';
    default: return 'default';
  }
};

// --- Edit Dialog Component ---
const EditTimeEntryDialog = ({ open, entry, onClose, onSave }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Breadcrumbs
  useBreadcrumbs(
       [
          { label: 'Zeiterfassung' },
        ]
  );

  useEffect(() => {
    if (entry) {
      setFormData({
        date: entry.date || '',
        hours: entry.hours || '',
        description: entry.description || '',
        project_name: entry.project_name || '',
        consultant_name: entry.consultant_name || '',
        contract_title: entry.contract_title || '',
        contract_status: entry.contract_status || '',
      });
      setErrors({});
    }
  }, [entry]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.date) newErrors.date = 'Datum ist erforderlich';
    if (!formData.hours || isNaN(Number(formData.hours)) || Number(formData.hours) <= 0) {
      newErrors.hours = 'Bitte gültige Stundenzahl eingeben';
    }
    return newErrors;
  };

  const handleSave = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      await onSave(entry.id, formData);
      onClose();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Zeiteintrag bearbeiten</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>

          {/* Read-only fields */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="caption" color="text.secondary">Projekt</Typography>
            <Typography variant="body1">{formData.project_name || '—'}</Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="caption" color="text.secondary">Vertrag</Typography>
            <Typography variant="body1">{formData.contract_title || '—'}</Typography>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Vertragsstatus</Typography>
            <Chip
              label={formData.contract_status || '—'}
              color={getStatusColor(formData.contract_status)}
              size="small"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="caption" color="text.secondary">Berater</Typography>
            <Typography variant="body1">{formData.consultant_name || '—'}</Typography>
          </Grid>



          {/* Divider */}
          <Grid size={{ xs: 12 }}>
            <Divider />
          </Grid>

          {/* Editable fields */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Datum"
              type="date"
              value={formData.date || ''}
              onChange={handleChange('date')}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!errors.date}
              helperText={errors.date}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Stunden"
              type="number"
              inputProps={{ min: 0, step: 0.25 }}
              value={formData.hours || ''}
              onChange={handleChange('hours')}
              fullWidth
              error={!!errors.hours}
              helperText={errors.hours}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label="Beschreibung"
              value={formData.description || ''}
              onChange={handleChange('description')}
              fullWidth
              multiline
              rows={6}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Abbrechen
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? 'Speichern…' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Component
const TimeTracking = () => {
  const theme = useTheme();
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarData, setCalendarData] = useState([]);
  const [tabValue, setTabValue] = useState(0);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  useEffect(() => {
    fetchTimeEntries();
  }, []);

  useEffect(() => {
    if (timeEntries.length > 0) {
      const entriesByDate = {};
      timeEntries.forEach(entry => {
        const date = entry.date;
        if (!entriesByDate[date]) {
          entriesByDate[date] = 0;
        }
        entriesByDate[date] += parseFloat(entry.hours);
      });

      const calendarData = Object.entries(entriesByDate).map(([date, value]) => ({
        day: date,
        value: value
      }));

      setCalendarData(calendarData);
    }
  }, [timeEntries]);

  const fetchTimeEntries = async () => {
    try {
      const response = await axiosInstance.get('/time-entries/', {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });

      const processedEntries = response.data.map(entry => ({
        ...entry,
        date: entry.date,
        formattedDate: dayjs(entry.date).locale('de').format('dd, DD.MM.YYYY'),
        sortableDate: entry.date
      }));

      processedEntries.sort((a, b) => new Date(b.sortableDate) - new Date(a.sortableDate));
      setTimeEntries(processedEntries);
    } catch (error) {
      console.error("Error loading time entries:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Möchten Sie diesen Zeiteintrag wirklich löschen?')) {
      return;
    }
    try {
      await axiosInstance.delete(`/time-entries/${id}/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      fetchTimeEntries();
    } catch (error) {
      console.error("Error deleting time entry:", error);
    }
  };

  const handleOpenEditDialog = (entry) => {
    setSelectedEntry(entry);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedEntry(null);
  };

  const handleSaveEntry = async (id, formData) => {
    await axiosInstance.patch(`/time-entries/${id}/`, formData, {
      headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
    });
    fetchTimeEntries();
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const columns = [
    {
      field: "actions",
      headerName: "Aktionen",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
          <Tooltip title="Eintrag bearbeiten">
            <IconButton
              variant="action-edit" // get style from theme.js
              onClick={() => handleOpenEditDialog(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eintrag löschen">
            <IconButton
              variant="action-delete" // get style from theme.js
              onClick={() => handleDeleteEntry(params.row.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "formattedDate",
      headerName: "Datum",
      width: 150,
      valueGetter: (value, row) => row.sortableDate,
      renderCell: (params) => params.row.formattedDate,
    },
    { field: "project_name", headerName: "Projekt", width: 200 },
    { field: "contract_title", headerName: "Vertrag", width: 200 },
    { field: "consultant_name", headerName: "Berater", width: 200 },
    {
      field: "contract_status",
      headerName: "Vertragsstatus",
      width: 160,
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Chip
            label={params.value}
            color={getStatusColor(params.value)}
            size="small"
          />
        </div>
      ),
    },
    { field: "hours", headerName: "Stunden", width: 100 },
    { field: "description", headerName: "Beschreibung", flex: 1 },

  ];

  return (
    <Box sx={{ padding: 0 }}>
      <Typography variant="h4" sx={{ marginBottom: 2 }} >
        Zeiterfassung
      </Typography>

      {/* Calendar chart */}
          <Paper 
            elevation={1}
            sx={{
              mb: 1,
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 2
            }}
          >
        <Box sx={{ height: 300, marginBottom: 3 }}>





          <Typography variant="h6" sx={{ marginBottom: 2 }}>
            Summierte Stunden pro Tag
          </Typography>
          {calendarData.length > 0 ? (
            <ResponsiveCalendar
            theme={nivoTheme(theme)}
              data={calendarData}
              from={dayjs().startOf('year').format('YYYY-MM-DD')}
              to={dayjs().endOf('year').format('YYYY-MM-DD')}
              emptyColor={theme.palette.tokens.surface2}
              /* Zeit-Domäne → Paar B (Teal-Ramp, D2-Chartregel); die Stufen
                 spiegeln sich im Dark Mode automatisch */
              colors={[
                theme.palette.tokens.ramp3[200],
                theme.palette.tokens.ramp3[300],
                theme.palette.tokens.ramp3[500],
                theme.palette.tokens.ramp3[700],
              ]}
              margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
              yearSpacing={40}
              monthBorderColor={theme.palette.tokens.surface}
              dayBorderWidth={2}
              dayBorderColor={theme.palette.tokens.surface}
              legends={[
                {
                  anchor: 'bottom-right',
                  direction: 'row',
                  translateY: 36,
                  itemCount: 4,
                  itemWidth: 42,
                  itemHeight: 36,
                  itemsSpacing: 14,
                  itemDirection: 'right-to-left'
                }
              ]}
            />
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography>Keine Daten für den Kalender verfügbar</Typography>
            </Box>
          )}

        </Box>
                  </Paper>

      {/* Tabbed view */}
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="time tracking tabs">
            <Tab label="Zeiteinträge" />
            <Tab label="Neuer Eintrag" />
          </Tabs>
        </Box>

        {/* Time Entries Tab */}
        <Box role="tabpanel" hidden={tabValue !== 0} sx={{ mt: 2 }}>
          {tabValue === 0 && (
            <Box sx={{
              height: 'calc(100vh - 600px)',
              minHeight: '300px'
            }}>
              <DataGrid
                rows={timeEntries}
                columns={columns}
                loading={loading}
                pageSize={5}
                rowsPerPageOptions={[5]}
                getRowId={(row) => row.id}
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'sortableDate', sort: 'desc' }],
                  },
                }}
                slots={{
                  noRowsOverlay: () => (
                    <EmptyState
                      title="Noch keine Zeiteinträge."
                      hint="Der erste Eintrag dauert keine Minute."
                      actionLabel="Neuen Eintrag anlegen"
                      onAction={() => setTabValue(1)}
                    />
                  ),
                  noResultsOverlay: NoResultsOverlay,
                }}
              />
            </Box>
          )}
        </Box>

        {/* Add New Entry Tab */}
        <Box role="tabpanel" hidden={tabValue !== 1} sx={{ mt: 2 }}>
          {tabValue === 1 && (
            <TimeEntryForm onEntryAdded={fetchTimeEntries} />
          )}
        </Box>
      </Box>

      {/* Edit Dialog */}
      <EditTimeEntryDialog
        open={editDialogOpen}
        entry={selectedEntry}
        onClose={handleCloseEditDialog}
        onSave={handleSaveEntry}
      />
    </Box>
  );
};

export default TimeTracking;